import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { format } from 'date-fns';
import { 
  Calendar, 
  MapPin, 
  DollarSign,
  Users,
  ExternalLink,
  Loader2,
  ChevronLeft,
  Trophy,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EventDetailPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvent();
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/${eventId}`);
      setEvent(response.data);
    } catch (error) {
      console.error('Failed to fetch event:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-bone pt-20">
        <div className="container-padding py-16 text-center">
          <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-display text-2xl text-deep-green mb-2">Event Not Found</h1>
          <Link to="/events">
            <Button className="mt-4">Back to Events</Button>
          </Link>
        </div>
      </div>
    );
  }

  const eventDate = new Date(event.start_at);

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-24">
      {/* Hero */}
      <div className="bg-deep-green py-12 md:py-16">
        <div className="container-padding">
          <Link to="/events" className="inline-flex items-center text-cream/70 hover:text-white mb-4">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Events
          </Link>
          
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <Badge className="bg-gold text-deep-green capitalize">
              {event.type?.replace('_', ' ')}
            </Badge>
            {event.format && (
              <Badge variant="outline" className="border-white/30 text-white">
                {event.format}
              </Badge>
            )}
          </div>
          
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-4">
            {event.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-6 text-cream/80">
            <span className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {format(eventDate, 'EEEE, MMMM d, yyyy')}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              {format(eventDate, 'h:mm a')}
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {event.city}, {event.state}
            </span>
          </div>
        </div>
      </div>

      <div className="container-padding py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Event Image */}
            {event.flyer_image_url && (
              <Card className="card-premium overflow-hidden">
                <img 
                  src={event.flyer_image_url} 
                  alt={event.title}
                  className="w-full h-auto"
                />
              </Card>
            )}

            {/* Details Card */}
            <Card className="card-premium">
              <CardContent className="p-6 space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Event Type</p>
                    <p className="font-semibold text-deep-green capitalize flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-gold" />
                      {event.type?.replace('_', ' ')}
                    </p>
                  </div>
                  
                  {event.format && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Format</p>
                      <p className="font-semibold text-deep-green capitalize">{event.format}</p>
                    </div>
                  )}
                  
                  {event.capacity && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Capacity</p>
                      <p className="font-semibold text-deep-green flex items-center gap-2">
                        <Users className="w-5 h-5 text-gold" />
                        {event.capacity} players
                      </p>
                    </div>
                  )}
                  
                  {event.end_at && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">End Time</p>
                      <p className="font-semibold text-deep-green">
                        {format(new Date(event.end_at), 'h:mm a')}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price & Register */}
            <Card className="card-premium">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Entry Fee</span>
                  <span className="text-2xl font-display font-bold text-gold">
                    {event.entry_fee === 0 || event.entry_fee === null 
                      ? 'Free' 
                      : `$${event.entry_fee}`
                    }
                  </span>
                </div>
                
                {event.registration_url ? (
                  <a 
                    href={event.registration_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full btn-gold">
                      Register Now
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                ) : (
                  <Button className="w-full btn-gold">
                    Register Now
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Location */}
            <Card className="card-premium">
              <CardContent className="p-6">
                <h3 className="font-display font-semibold text-lg text-deep-green mb-4">Location</h3>
                <p className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="w-5 h-5 shrink-0 mt-0.5" />
                  {event.city}, {event.state}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailPage;
